import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useOrder } from "../context/OrderContext";
import { API_URL, API_BASE_URL, VENUE_ID, getOrCreateCustomerToken } from "../config/constants";
import { PENDING_ORDER_KEY } from "../context/OrderContext";
import "./PaymentReturn.css";

const PAYMOB_PENDING_KEY = "paymob_pending";

function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setActiveOrder, setIsProcessing, socket } = useOrder();
  const [status, setStatus] = useState("verifying"); // verifying | success | error

  useEffect(() => {
    const run = async () => {
      const pending = sessionStorage.getItem(PAYMOB_PENDING_KEY);
      if (!pending) {
        toast.error("Session expired. Please try again.");
        setStatus("error");
        setTimeout(() => navigate("/"), 2000);
        return;
      }

      let cartData;
      try {
        cartData = JSON.parse(pending);
      } catch (e) {
        sessionStorage.removeItem(PAYMOB_PENDING_KEY);
        toast.error("Invalid session data.");
        setStatus("error");
        setTimeout(() => navigate("/"), 2000);
        return;
      }

      const params = {};
      searchParams.forEach((v, k) => { params[k] = v; });

      if (Object.keys(params).length === 0) {
        toast.error("No payment data received.");
        setStatus("error");
        sessionStorage.removeItem(PAYMOB_PENDING_KEY);
        setTimeout(() => navigate("/"), 2000);
        return;
      }

      try {
        const verifyResp = await axios.post(
          `${API_BASE_URL}/api/paymob/verify-payment`,
          params,
          { timeout: 10000 }
        );

        if (!verifyResp.data.success) {
          const msg =
            verifyResp.data.reason === "declined"
              ? "Payment was declined. Please try another card or payment method."
              : "Payment verification failed.";
          toast.error(msg);
          setStatus("error");
          sessionStorage.removeItem(PAYMOB_PENDING_KEY);
          setTimeout(() => navigate("/"), 2000);
          return;
        }
        if (!verifyResp.data.payment_id) {
          toast.error("Payment verification failed.");
          setStatus("error");
          sessionStorage.removeItem(PAYMOB_PENDING_KEY);
          setTimeout(() => navigate("/"), 2000);
          return;
        }

        const orderItems = cartData.items.map((item) => ({
          recipe_id: item.recipe_id,
          quantity: item.quantity,
          unit_price: item.recipe_price,
          toppings_json: null,
        }));

        const response = await axios.post(`${API_URL}/graphql`, {
          query: `
            mutation CreateOrder($input: CreateOrderInput!) {
              createOrder(input: $input) {
                order_id
                order_number
                order_status
                total_amount
                items {
                  item_id
                  recipe_id
                  quantity
                  recipe { recipe_name }
                }
              }
            }
          `,
          variables: {
            input: {
              customer_name: cartData.customer_name || "Customer",
              customer_mobile: cartData.customer_mobile || "+911234567890",
              customer_email: cartData.customer_email || null,
              customer_token: getOrCreateCustomerToken(),
              order_type: "customer_kiosk",
              payment_method: "paymob",
              payment_id: verifyResp.data.payment_id,
              kiosk_type: "customer",
              venue_id: VENUE_ID,
              items: orderItems,
            },
          },
        });

        sessionStorage.removeItem(PAYMOB_PENDING_KEY);

        if (response.data.data?.createOrder) {
          const order = response.data.data.createOrder;
          setActiveOrder(order);
          setIsProcessing(true);
          setStatus("success");
          sessionStorage.setItem(PENDING_ORDER_KEY, String(order.order_id));
          toast.success("Order placed successfully!");
          if (socket) {
            socket.emit("subscribeToOrder", { orderId: order.order_id });
          }
          setTimeout(() => navigate(`/order-status/${order.order_id}`), 500);
        } else {
          throw new Error(response.data.errors?.[0]?.message || "Failed to create order");
        }
      } catch (error) {
        const err = error.response?.data?.errors?.[0]?.message || error.response?.data?.error || error.message;
        const isNetwork = /timeout|network|ECONNREFUSED|ERR_NETWORK/i.test(String(err));
        toast.error(isNetwork ? "Connection issue. Please check your network and try again." : (err || "Payment failed."));
        setStatus("error");
        sessionStorage.removeItem(PAYMOB_PENDING_KEY);
        setTimeout(() => navigate("/"), 2000);
      }
    };

    run();
  }, [searchParams, navigate, setActiveOrder, setIsProcessing, socket]);

  return (
    <div className="payment-return-page">
      <div className="payment-return-card">
        {status === "verifying" && (
          <>
            <div className="payment-spinner">⏳</div>
            <h2>Verifying payment...</h2>
            <p>Please wait.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="payment-success">✓</div>
            <h2>Payment successful!</h2>
            <p>Redirecting to order status...</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="payment-error">✕</div>
            <h2>Payment failed</h2>
            <p>Redirecting to menu...</p>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentReturn;
